import { BRANDS_WITH_REQUIRED_FREIGHT } from '@cannect/utils/constants'
import LoadingContainer from 'components/LoadingContainer'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DocumentConfirm } from '../../assets/svg/sendDocuments'
import PowerOfAttorneySideSheet from '../../components/SideSheet/PowerOfAttorneySideSheet'
import { SendPrescription } from '../../new-components/SendDocument'
import { StepsSendDocuments } from '../../new-components/atoms'

interface RouteParams {
  order_id?: string
}

interface SendLaudoMedicoUIProps {
  canContinue: any
  handleContinue: any
  anvisaFiles: any[]
  orderData: any
  orderLoading: any
  existingDocuments: any[]
  deleteDocument: any
  handleAnvisaDrop: any
  handleAnvisaFileSelect: any
  handleTakePhoto: any
  handleDragOver: any
  handleDragLeave: any
  setCurrentStep: any
  handleGenerateDocument: any
  widget: any
  signatureStatus: any
  clearWidget: any
  resetSignatureStatus: any
  sendDocuments: any
  laudoMedicoUploadState: any
  documentRefused: any
  setDocumentRefused: any
  hasUploaded: any
  setHasUploaded: any
  responseType: 'normal' | 'ocr'
  isLoading: boolean
  handleSignDocument: () => Promise<any>
  handleDownloadDocument: () => Promise<any>
  handleDownloadStepByStep: () => void
  handleAnvisaSubmit: () => Promise<void>
  /** Sobrescreve o redirect padrão quando o pedido não requer esta etapa */
  onInvalidOrderStatus?: () => void
  /** Associação exige laudo médico independente do tipo de produto */
  alwaysRequireLaudo?: boolean
}

export const SendLaudoMedicoUI: React.FC<SendLaudoMedicoUIProps> = ({
  canContinue,
  handleContinue,
  anvisaFiles,
  orderData,
  orderLoading,
  existingDocuments,
  deleteDocument,
  handleAnvisaDrop,
  handleAnvisaFileSelect,
  handleTakePhoto,
  handleDragOver,
  handleDragLeave,
  setCurrentStep: setHookCurrentStep,
  handleGenerateDocument,
  widget,
  signatureStatus,
  clearWidget,
  resetSignatureStatus,
  sendDocuments,
  laudoMedicoUploadState,
  documentRefused,
  setDocumentRefused,
  hasUploaded,
  setHasUploaded,
  responseType,
  isLoading,
  handleSignDocument,
  handleDownloadDocument,
  handleDownloadStepByStep,
  handleAnvisaSubmit,
  onInvalidOrderStatus,
  alwaysRequireLaudo = false
}: SendLaudoMedicoUIProps) => {
  const history = useHistory()

  const [currentStep, setCurrentStep] = useState(3)
  const [showPowerOfAttorneySideSheet, setShowPowerOfAttorneySideSheet] = useState(false)

  useEffect(() => {
    setHookCurrentStep('laudo_medico')
  }, [setHookCurrentStep])

  useEffect(() => {
    if (orderData && orderData.status) {
      const allowedStatuses = ['pending', 'approved', 'processing']
      if (!allowedStatuses.includes(orderData.status)) {
        toast.info('Este pedido não requer laudo médico')
        if (onInvalidOrderStatus) {
          onInvalidOrderStatus()
        } else {
          history.push('/painel-do-usuario')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, history])

  if (orderLoading) {
    return <LoadingContainer loading={orderLoading} />
  }

  const handleGeneratePowerOfAttorney = async () => {
    setShowPowerOfAttorneySideSheet(true)
  }

  if (!orderData) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '20px 0' }}>
        <div style={{ height: '100vh', backgroundColor: '#f5f5f5', padding: '20px 0' }}>
          <div style={{ textAlign: 'center', padding: '40px', color: '#666', fontSize: '1.1rem' }}>
            Pedido não encontrado. Redirecionando...
          </div>
        </div>
      </div>
    )
  }

  const items = orderData?.order?.items.filter((item: any) => item.product?.type === 'PRODUTO') || []

  const hasLaudoProducts =
    alwaysRequireLaudo ||
    items.some((item: any) => item.product?.delivery_by_association === true || item.class_id === 4)

  const allProductsAreSupplements = items.every((item: any) => item.class_id === 6)

  const allProductsAreFreightBrand = items.every((item: any) => BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id))

  const allProductsDontNeedAnvisa = items.every(
    (item: any) =>
      item.product?.delivery_by_association === true ||
      item.class_id === 4 ||
      item.class_id === 6 ||
      BRANDS_WITH_REQUIRED_FREIGHT.includes(item.brand_id)
  )

  let customSteps

  if (alwaysRequireLaudo && !allProductsDontNeedAnvisa) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Autorização Anvisa' },
      { number: 5, text: 'Dados da entrega' }
    ]
  } else if (alwaysRequireLaudo) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Dados da entrega' }
    ]
  } else if (allProductsAreSupplements) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Dados da entrega' }
    ]
  } else if (allProductsAreFreightBrand) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Dados da entrega' }
    ]
  } else if (allProductsDontNeedAnvisa && hasLaudoProducts) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Dados da entrega' }
    ]
  } else if (hasLaudoProducts && !allProductsDontNeedAnvisa) {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Laudo Médico' },
      { number: 4, text: 'Autorização Anvisa' },
      { number: 5, text: 'Dados da entrega' }
    ]
  } else {
    customSteps = [
      { number: 1, text: 'Envio de receita' },
      { number: 2, text: 'Envio de documentos' },
      { number: 3, text: 'Autorização Anvisa' },
      { number: 4, text: 'Dados da entrega' }
    ]
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{ padding: '20px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <StepsSendDocuments
            currentStep={currentStep}
            automaticReadingEnabled={false}
            onToggleAutomaticReading={undefined}
            customSteps={customSteps}
          />
        </div>
      </div>

      <div style={{ padding: '20px 0' }}>
        <div style={{ width: '100%', padding: '0 20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #93c5fd'
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '14px',
                flexShrink: 0
              }}>
              !
            </div>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>ATENÇÃO: </span>O laudo médico solicitado
              abaixo é necessário para o processamento do seu pedido. Mas não se preocupe! Caso não possa anexá-lo
              agora, você poderá enviá-lo posteriormente.
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 0' }}>
        <div style={{ width: '100%', padding: '0 20px' }}>
          <SendPrescription
            onDrop={handleAnvisaDrop}
            onFileSelect={handleAnvisaFileSelect}
            onTakePhoto={handleTakePhoto}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            multiple
            supportedFiles={['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png']}
            maxSize="10MB"
            title="Envie seu Laudo Médico"
            description="Estamos muito próximos de concluir o processo! Por favor, envie o laudo médico necessário. Assim, poderemos validar todas as informações necessárias e garantir que o pedido seja processado com total segurança."
            yellowLabelInformation
            descriptionInformation="Você pode enviar o laudo médico agora ou deixar para depois. Se precisar de ajuda, um atendente irá te auxiliar."
            anvisaButtons={false}
            handleGeneratePowerOfAttorney={handleGeneratePowerOfAttorney}
            handleDownloadStepByStep={handleDownloadStepByStep}
            loading={isLoading}
            stepName="laudo_medico"
            requestFile={anvisaFiles[0]}
            responseType={responseType}
            existingDocuments={
              existingDocuments
                ? existingDocuments.filter((doc) => doc.type === 'LAUDO_MEDICO' || doc.type === 'ANVISA')
                : []
            }
            onDeleteDocument={deleteDocument}
            sendDocuments={sendDocuments}
            uploadState={laudoMedicoUploadState}
            documentRefused={documentRefused}
            setDocumentRefused={setDocumentRefused}
            hasUploaded={hasUploaded}
            setHasUploaded={setHasUploaded}
            icon={<DocumentConfirm />}
          />
        </div>
      </div>

      <div style={{ padding: '40px 0', textAlign: 'end' }}>
        <div style={{ width: '100%', padding: '0 20px' }}>
          <button
            onClick={handleContinue}
            disabled={isLoading || !canContinue()}
            style={{
              background: isLoading || !canContinue() ? '#ccc' : '#748351',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '16px 48px',
              fontSize: '1.1rem',
              fontWeight: 600,
              cursor: isLoading || !canContinue() ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              minWidth: '200px'
            }}>
            {isLoading ? 'Processando...' : canContinue() ? 'Continuar' : 'Aguarde o documento'}
          </button>
        </div>
      </div>

      <PowerOfAttorneySideSheet
        isOpen={showPowerOfAttorneySideSheet}
        onClose={() => setShowPowerOfAttorneySideSheet(false)}
        onSignDocument={handleSignDocument}
        onDownloadDocument={handleDownloadDocument}
        handleGenerateDocument={handleGenerateDocument}
        widget={widget}
        signatureStatus={signatureStatus}
        clearWidget={clearWidget}
        resetSignatureStatus={resetSignatureStatus}
      />
    </div>
  )
}
